import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LockKeyhole } from "lucide-react";
import { IoEyeOffOutline } from "react-icons/io5";
import { FaRegEye } from "react-icons/fa";


const customStyles = `
  .reset-password-input input[type="text"],
  .reset-password-input input[type="password"] {
    padding: 16px 40px !important;
  }
`;


const ResetPassword = () => {
  const accessToken = localStorage.getItem("accessToken");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!oldPassword || !newPassword || !newPasswordConfirm) {
      setError("All fields are required.");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError("New password and confirm password do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${accessToken}`);
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirm,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/employees/change-password`,
        requestOptions
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess("Password changed successfully!");
        setOldPassword("");
        setNewPassword("");
        setNewPasswordConfirm("");
      } else {
        setError(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 md:border md:p-8 w-full max-w-md rounded-lg mx-4 shadow-2xl"
      >
        <style>{customStyles}</style>
        <div className="text-center">
          <h1 className="font-extrabold text-2xl mb-2">Reset Password</h1>
          <p className="text-sm text-gray-600">Enter your new password</p>
        </div>

        {/* Old Password Section */}
        <div className="relative w-full reset-password-input">
          <input
            type={showOldPassword ? "text" : "password"}
            value={oldPassword}
            placeholder="Enter your old password"
            className="w-full border rounded"
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <LockKeyhole
            className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-600 pointer-events-none"
            size={20}
          />
          {showOldPassword ? (
            <IoEyeOffOutline
              onClick={() => setShowOldPassword(false)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          ) : (
            <FaRegEye
              onClick={() => setShowOldPassword(true)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          )}
        </div>

        {/* New Password Section */}
        <div className="relative w-full reset-password-input">
          <input
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            placeholder="Enter your new password"
            className="w-full border rounded"
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <LockKeyhole
            className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-600 pointer-events-none"
            size={20}
          />
          {showNewPassword ? (
            <IoEyeOffOutline
              onClick={() => setShowNewPassword(false)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          ) : (
            <FaRegEye
              onClick={() => setShowNewPassword(true)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          )}
        </div>

        {/* Confirm Password Section */}
        <div className="relative w-full reset-password-input">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={newPasswordConfirm}
            placeholder="Confirm your password"
            className="w-full border rounded"
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
          />
          <LockKeyhole
            className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-600 pointer-events-none"
            size={20}
          />
          {showConfirmPassword ? (
            <IoEyeOffOutline
              onClick={() => setShowConfirmPassword(false)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          ) : (
            <FaRegEye
              onClick={() => setShowConfirmPassword(true)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-600 cursor-pointer"
              size={20}
            />
          )}
        </div>

        {/* Error Message */}
        {error && (
          <span className="text-xs text-center text-red-600 font-light">{error}</span>
        )}

        {/* Success Message */}
        {success && (
          <span className="text-xs text-center text-green-600 font-light">{success}</span>
        )}

        {/* Submit Button */}
        {loading ? (
          <button
            type="button"
            className="bg-primary-hrms text-white flex items-center justify-center h-12"
            disabled
          >
            <Loader2 className="mr-2 h-6 w-6 animate-spin text-white" />
            Please wait
          </button>
        ) : (
          <button
            type="submit"
            className="bg-primary-hrms p-2 text-white h-12"
          >
            Reset
          </button>
        )}

        {/* Back to Login */}
        <span className="text-center">
          Back to{" "}
          <Link className="text-blue-500 hover:underline" to="/">
            Login
          </Link>
        </span>
      </form>
    </div>
  );
};

export default ResetPassword;